import { Send } from "@mui/icons-material";
import * as uuid from "uuid";
import base64 from "base-64";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Coverage,
  Organization,
  OrganizationAffiliation,
  Patient,
  PractitionerRole,
} from "fhir/r5";
import { useState } from "react";
import { XmlDialog } from "./XmlDialog";
import {
  OperatorDataSection,
  BeneficiaryDataSection,
  DiagnosticHypothesisSection,
  ContractedDataSection,
  ExecutingProfessionalSection,
  AttendanceDataSection,
  ProcedureSection,
  ObservationsSection,
} from "./guide";
import { GuideDetailsData } from "./GuideEmissionTab";
import { oids } from "@/configs/Settings";
import { toast } from "react-toastify";
import { ValidateTiss } from "@/services/tiss/ValidateTiss";
import { PostTiss } from "@/services/tiss/PostTiss";
import { useValueSet } from "@/hooks/useValueSet";

export interface ProcedureData {
  codigoTabelaReferencia: string;
  codigoProcedimento: string;
  descricaoProcedimento: string;
  valorProcedimento: string;
  sequencialItem: number;
}

export interface ConsultationGuideData {
  registroANS: string;
  nomeOperadora: string;
  numeroGuiaPrestador: string;
  numeroGuiaOperadora: string;
  dataEmissao: dayjs.Dayjs | null;
  numeroLote: string;
  nomeBeneficiario: string;
  numeroCarteira: string;
  atendimentoRN: string;
  identificadorBeneficiario: string;
  nomeContratado: string;
  tipoDocumentoContratado: string;
  numeroDocumentoContratado: string;
  cnes: string;
  nomeProfissional: string;
  conselhoProfissional: string;
  numeroConselhoProfissional: string;
  ufConselho: string;
  cbos: string;
  indicacaoAcidente: string;
  dataAtendimento: dayjs.Dayjs | null;
  tipoConsulta: string;
  regimeAtendimento: string;
  coberturaEspecial: string;
  saudeOcupacional: string;
  procedimentos: ProcedureData[];
  observacao: string;
}

interface GuideDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  details: GuideDetailsData | null;
  ufOptions: any;
}

// Atualizando a interface GuideDetailsData para incluir o paciente

export function GuideDetailsDialog({
  open,
  onClose,
  details,
  ufOptions,
}: GuideDetailsDialogProps) {
  const generateTissXml = (data: ConsultationGuideData): string => {
    // Formatação das datas no padrão AAAA-MM-DD
    const generateUniqueId = (id: string) => {
      return base64.encode(id);
    };

    // Gerar hash MD5 dos dados (implementação simplificada)
    const generateHash = () => {
      // TODO: Implementar hash MD5 real dos dados
      return "96FE46D4329D53D9D297C3318FF7E100";
    };

    // Construir o XML como string
    const xml = `<?xml version="1.0" encoding="ISO-8859-1"?>
<ans:mensagemTISS xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.ans.gov.br/padroes/tiss/schemas http://www.ans.gov.br/padroes/tiss/schemas/tissV3_05_00.xsd"
  xmlns:ans="http://www.ans.gov.br/padroes/tiss/schemas">
  <ans:cabecalho>
    <ans:identificacaoTransacao>
      <ans:tipoTransacao>ENVIO_LOTE_GUIAS</ans:tipoTransacao>
      <ans:sequencialTransacao>1</ans:sequencialTransacao>
      <ans:dataRegistroTransacao>${dayjs().format(
        "YYYY-MM-DD"
      )}</ans:dataRegistroTransacao>
      <ans:horaRegistroTransacao>${dayjs().format(
        "HH:mm:ss"
      )}</ans:horaRegistroTransacao>
    </ans:identificacaoTransacao>
    <ans:origem>
      <ans:identificacaoPrestador>
        <ans:CNPJ>${data.numeroDocumentoContratado}</ans:CNPJ>
      </ans:identificacaoPrestador>
    </ans:origem>
    <ans:destino>
      <ans:registroANS>${data.registroANS}</ans:registroANS>
    </ans:destino>
    <ans:Padrao>3.05.00</ans:Padrao>
  </ans:cabecalho>
  <ans:prestadorParaOperadora>
    <ans:loteGuias>
      <ans:numeroLote>${data.numeroLote}</ans:numeroLote>
      <ans:guiasTISS>
        <ans:guiaSP-SADT>
          <ans:cabecalhoGuia>
            <ans:registroANS>${data.registroANS}</ans:registroANS>
            <ans:numeroGuiaPrestador>${
              data.numeroGuiaPrestador || Math.floor(Math.random() * 10000)
            }</ans:numeroGuiaPrestador>
            <ans:guiaPrincipal>${Math.floor(
              Math.random() * 1000000
            )}</ans:guiaPrincipal>
          </ans:cabecalhoGuia>
          <ans:dadosAutorizacao>
            <ans:numeroGuiaOperadora>${
              data.numeroGuiaOperadora
            }</ans:numeroGuiaOperadora>
            <ans:dataAutorizacao>${data.dataEmissao?.format(
              "YYYY-MM-DD"
            )}</ans:dataAutorizacao>
            <ans:senha>${Math.floor(Math.random() * 1000000)}</ans:senha>
          </ans:dadosAutorizacao>
          <ans:dadosBeneficiario>
            <ans:numeroCarteira>${data.numeroCarteira}</ans:numeroCarteira>
            <ans:atendimentoRN>${data.atendimentoRN}</ans:atendimentoRN>
            <ans:nomeBeneficiario>${
              data.nomeBeneficiario
            }</ans:nomeBeneficiario>
            <ans:identificadorBeneficiario>${generateUniqueId(
              data.identificadorBeneficiario
            )}</ans:identificadorBeneficiario>
          </ans:dadosBeneficiario>
          <ans:dadosSolicitante>
            <ans:contratadoSolicitante>
              <ans:cnpjContratado>${
                data.numeroDocumentoContratado
              }</ans:cnpjContratado>
              <ans:nomeContratado>${data.nomeContratado}</ans:nomeContratado>
            </ans:contratadoSolicitante>
            <ans:profissionalSolicitante>
              <ans:nomeProfissional>${
                data.nomeProfissional
              }</ans:nomeProfissional>
              <ans:conselhoProfissional>${
                data.conselhoProfissional
              }</ans:conselhoProfissional>
              <ans:numeroConselhoProfissional>${
                data.numeroConselhoProfissional
              }</ans:numeroConselhoProfissional>
              <ans:UF>${data.ufConselho}</ans:UF>
              <ans:CBOS>${data.cbos}</ans:CBOS>
            </ans:profissionalSolicitante>
          </ans:dadosSolicitante>
          <ans:dadosSolicitacao>
            <ans:caraterAtendimento>1</ans:caraterAtendimento>
          </ans:dadosSolicitacao>
          <ans:dadosExecutante>
            <ans:contratadoExecutante>
              <ans:cnpjContratado>${
                data.numeroDocumentoContratado
              }</ans:cnpjContratado>
              <ans:nomeContratado>${data.nomeContratado}</ans:nomeContratado>
            </ans:contratadoExecutante>
            <ans:CNES>${data.cnes}</ans:CNES>
          </ans:dadosExecutante>
          <ans:dadosAtendimento>
            <ans:tipoAtendimento>01</ans:tipoAtendimento>
            <ans:indicacaoAcidente>${
              data.indicacaoAcidente
            }</ans:indicacaoAcidente>
            <ans:tipoConsulta>${data.tipoConsulta}</ans:tipoConsulta>
          </ans:dadosAtendimento>
          <ans:procedimentosExecutados>
            ${data.procedimentos
              .map(
                (proc) => `
            <ans:procedimentoExecutado>
              <ans:sequencialItem>${proc.sequencialItem}</ans:sequencialItem>
              <ans:dataExecucao>${data.dataAtendimento?.format(
                "YYYY-MM-DD"
              )}</ans:dataExecucao>
              <ans:horaInicial>${dayjs().format("HH:mm:ss")}</ans:horaInicial>
              <ans:horaFinal>${dayjs().format("HH:mm:ss")}</ans:horaFinal>
              <ans:procedimento>
                <ans:codigoTabela>${
                  proc.codigoTabelaReferencia
                }</ans:codigoTabela>
                <ans:codigoProcedimento>${
                  proc.codigoProcedimento
                }</ans:codigoProcedimento>
                <ans:descricaoProcedimento>${
                  proc.descricaoProcedimento
                }</ans:descricaoProcedimento>
              </ans:procedimento>
              <ans:quantidadeExecutada>1</ans:quantidadeExecutada>
              <ans:viaAcesso>2</ans:viaAcesso>
              <ans:reducaoAcrescimo>0</ans:reducaoAcrescimo>
              <ans:valorUnitario>${proc.valorProcedimento}</ans:valorUnitario>
              <ans:valorTotal>${proc.valorProcedimento}</ans:valorTotal>
            </ans:procedimentoExecutado>
            `
              )
              .join("")}
          </ans:procedimentosExecutados>
          <ans:valorTotal>
            <ans:valorTotalGeral>${data.procedimentos
              .reduce(
                (total, proc) => total + parseFloat(proc.valorProcedimento),
                0
              )
              .toFixed(2)}</ans:valorTotalGeral>
          </ans:valorTotal>
        </ans:guiaSP-SADT>
      </ans:guiasTISS>
    </ans:loteGuias>
  </ans:prestadorParaOperadora>
  <ans:epilogo>
    <ans:hash>96FE46D4329D53D9D297C3318FF7E100</ans:hash>
  </ans:epilogo>
</ans:mensagemTISS>`;

    return xml;
  };

  // Função para extrair o valor do identificador do paciente
  const getPatientCPF = (patient?: Patient): string => {
    if (!patient?.identifier?.length) return "";
    const taxIdentifier = patient.identifier.find((id: any) =>
      id.system?.includes(oids.cpf)
    );
    return taxIdentifier?.value || "";
  };

  const getCoverageId = (coverage: Coverage): string => {
    if (!coverage?.identifier?.length) return "";

    return coverage?.identifier?.[0]?.value || "";
  };

  // Função para obter o nome do paciente
  const getPatientName = (patient: any): string => {
    if (!patient?.name?.length) return "";
    const officialName = patient.name.find((n: any) => n.use === "official");
    return officialName?.text || "";
  };

  // Função para obter o registro ANS da operadora
  const getANSRegistration = (organization: any): string => {
    if (!organization?.identifier?.length) return "";
    const ansIdentifier = organization.identifier.find(
      (id: any) => id.system === "https://fhir.ans.gov.br/sid/registro-ans"
    );
    return ansIdentifier?.value || "";
  };

  // Função para extrair o código CBO da especialidade
  const getCBOCode = (practitioner: any): string => {
    if (!practitioner?.specialty?.length) return "";

    const specialty = practitioner.specialty[0];
    if (!specialty?.coding?.length) return "";

    const cboCoding = specialty.coding.find((coding: any) =>
      coding.system?.includes("CBO-S")
    );

    return cboCoding?.code || "";
  };

  // Função para extrair informações do conselho profissional
  const getProfessionalCouncilInfo = (practitioner: PractitionerRole) => {
    if (!practitioner?.identifier?.length)
      return { council: "", number: "", state: "" };

    const councilIdentifier = practitioner.identifier?.[0];

    if (!councilIdentifier) return { council: "", number: "", state: "" };

    const system = councilIdentifier.system?.toUpperCase() || "";
    const value = councilIdentifier.value || "";

    // Mapeamento de siglas para códigos do conselho
    const councilCodes: { [key: string]: string } = {
      CRM: "06", // Conselho Regional de Medicina
      COREN: "02", // Conselho Regional de Enfermagem
      CRO: "08", // Conselho Regional de Odontologia
      CREFITO: "05", // Conselho Regional de Fisioterapia
      CRP: "09", // Conselho Regional de Psicologia
    };

    // Encontra o código do conselho baseado na sigla
    const councilCode =
      Object.entries(councilCodes).find(([key]) => system.includes(key))?.[1] ||
      "";

    // Extrai UF e número do valor (assumindo formato SIGLA/UF123456)
    const matches = value.match(/[A-Z]{2,}\/?([A-Z]{2})(\d+)/);
    const state =
      ufOptions?.find((uf: any) => uf.display === matches?.[1])?.value || "";
    const number = matches?.[2] || value; // Se não conseguir extrair, usa o valor completo

    return {
      council: councilCode,
      number: number,
      state: state,
    };
  };

  // Função para obter o CNPJ da operadora
  const getCNPJ = (organization?: Organization): string => {
    if (!organization?.identifier?.length) return "";
    const cnpjIdentifier = organization.identifier.find((id: any) =>
      id.system?.includes(oids.cnpj)
    );
    return cnpjIdentifier?.value || "";
  };

  // Função para obter o CNPJ da operadora
  const getCnes = (organization: Organization): string => {
    if (!organization?.identifier?.length) return "";
    const cnesIdentifier = organization.identifier.find((id: any) =>
      id.system?.includes("cnes")
    );
    return cnesIdentifier?.value || "";
  };

  // Função para extrair informações dos procedimentos da affiliation
  const getProceduresInfo = (
    affiliation: OrganizationAffiliation
  ): ProcedureData[] => {
    if (!affiliation?.specialty?.length) return [];

    return affiliation.specialty.map((specialty, index) => {
      const coding = specialty.coding?.[0];
      const value =
        specialty.extension?.find(
          (ext) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/service-value"
        )?.valueMoney?.value || 0;

      const isTUSS = coding?.system?.includes("TUSSProcedimentosEventos");

      return {
        codigoTabelaReferencia: isTUSS ? "22" : "Tabela Própria das O",
        codigoProcedimento: coding?.code || "",
        descricaoProcedimento: coding?.display || "",
        valorProcedimento: value.toFixed(2),
        sequencialItem: index + 1,
      };
    });
  };

  const [formData, setFormData] = useState<ConsultationGuideData>({
    // Dados da Operadora
    registroANS: details?.participatingOrganization
      ? getANSRegistration(details.participatingOrganization)
      : "",
    nomeOperadora: details?.participatingOrganization?.name || "",
    numeroGuiaPrestador: `CSS${dayjs().format("YYMMDDHHmmss")}`,
    numeroGuiaOperadora: "",
    dataEmissao: dayjs(),
    numeroLote: "1",

    // Dados do Beneficiário
    nomeBeneficiario: details?.patient ? getPatientName(details.patient) : "",
    numeroCarteira: details?.coverage ? getCoverageId(details.coverage) : "",
    atendimentoRN: "N",
    identificadorBeneficiario: details?.patient
      ? getPatientCPF(details.patient)
      : "",

    // Dados do Contratado
    nomeContratado: details?.primaryOrganization?.name || "",
    tipoDocumentoContratado: "CNPJ",
    numeroDocumentoContratado: details?.primaryOrganization
      ? getCNPJ(details.primaryOrganization)
      : "",
    cnes: details?.primaryOrganization
      ? getCnes(details.primaryOrganization)
      : "",

    // Dados do Profissional
    nomeProfissional: details?.practitioner?.practitioner?.display || "",
    conselhoProfissional: details?.practitioner
      ? getProfessionalCouncilInfo(details.practitioner).council
      : "",
    numeroConselhoProfissional: details?.practitioner
      ? getProfessionalCouncilInfo(details.practitioner).number
      : "",
    ufConselho: details?.practitioner
      ? getProfessionalCouncilInfo(details.practitioner).state
      : "",
    cbos: details?.practitioner ? getCBOCode(details.practitioner) : "",

    // Dados do Atendimento
    indicacaoAcidente: "9", // Não Acidente por padrão
    dataAtendimento: details?.appointment?.start
      ? dayjs(details.appointment.start)
      : dayjs(),
    tipoConsulta: "1", // Primeira consulta por padrão
    regimeAtendimento: details?.appointment?.virtualService?.length
      ? "Telessaúde"
      : "Ambulatorial",
    coberturaEspecial: "",
    saudeOcupacional: "",

    // Dados dos Procedimentos
    procedimentos: details?.affiliation
      ? getProceduresInfo(details.affiliation)
      : [],
    observacao: "",
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    formData: ConsultationGuideData
  ) => {
    event.preventDefault();

    // Gerar o XML a partir dos dados do formulário
    try {
      setLoading(true);
      const xml = generateTissXml(formData);
      await ValidateTiss(xml);
      try {
        const tissData = {
          organizationId: getCNPJ(details?.primaryOrganization),
          organizationName: details?.primaryOrganization?.name || "",
          organizationAlias:
            details?.primaryOrganization?.alias?.[0] ||
            details?.primaryOrganization?.name ||
            "",
          practitionerId:
            details?.practitioner?.practitioner?.reference?.split("-")[1] || "",
          practitionerName: details?.practitioner?.practitioner?.display || "",
          patientId: getPatientCPF(details?.patient) || "",
          appointmentId: details?.appointment?.id || "",
        };
        await PostTiss(tissData, xml);
        toast.success("Guia TISS foi emitida com sucesso");
      } catch (error) {
        toast.warning(
          "Não foi possível emitir a guia, por favor tente novamente."
        );
        console.error("Erro ao validar o XML:", error);
      }
    } catch (error) {
      console.error("Erro ao gerar o XML:", error);
      toast.warning(
        "Não foi possível validar o XML, por favor tente novamente."
      );
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Detalhes da Consulta</DialogTitle>
        <DialogContent>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
          >
            <OperatorDataSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <BeneficiaryDataSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <DiagnosticHypothesisSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <ContractedDataSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <ExecutingProfessionalSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <AttendanceDataSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <ProcedureSection
              formData={formData}
              affiliation={details?.affiliation || null}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
            <ObservationsSection
              formData={formData}
              onFieldChange={(name, value) =>
                setFormData({ ...formData, [name]: value })
              }
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            disabled={loading}
            color="primary"
            onClick={(e) => handleSubmit(e as any, formData)}
          >
            Emitir Guia
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
